import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Notification from "../../components/Notification.js";
import { API_URL } from "../../config.js";
import { allowedToView } from "../../utils/PermissionUtils.js";
import { formatTimestamp, formatToDayMonthYear } from "../../utils/TextUtils.js";

function MilkCollections() {
    const [milkCollections, setMilkCollections] = useState([]);
    const [totalLitres, setTotalLitres] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [selectedMilkCollection, setSelectedMilkCollection] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filterDate, setFilterDate] = useState(""); // Filter by specific date
    const [filterMonth, setFilterMonth] = useState(""); // Filter by month and year
    const [filterRoute, setFilterRoute] = useState(""); // Filter by route (via farmer's route_id)
    const [filterShift, setFilterShift] = useState(""); // Filter by shift
    const [routes, setRoutes] = useState([]); // List of routes from API
    const perPage = 10; // Items per page
    const token = localStorage.getItem("access_token");
    const userRole = localStorage.getItem("userRole");
    
    // Define shifts array
    const shifts = [
        { value: "", label: "All Shifts" },
        { value: "Morning", label: "Morning" },
        { value: "Evening", label: "Evening" }
    ];

    const canEdit = (userRole) => {
        return (allowedToView(userRole, ['admin', 'superadmin']));
    };

    const navigate = useNavigate();

    // Fetch routes from API
    const fetchRoutes = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/collection-routes`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setRoutes(data || []);
            }
        } catch (error) {
            console.error("Error fetching routes:", error);
        }
    }, [token]);

    // Memoize fetchMilkCollections to prevent unnecessary re-renders
    const fetchMilkCollections = useCallback(async () => {
        try {
            setLoading(true);
            let url = `${API_URL}/milk-collections?page=${currentPage}&limit=${perPage}`;
            if (searchTerm) {
                url += `&search=${searchTerm}`;
            }
            if (filterDate) {
                url += `&date=${filterDate}`;
            }
            if (filterMonth) {
                url += `&month=${filterMonth}`;
            }
            if (filterRoute) {
                url += `&route_id=${filterRoute}`; 
            }
            if (filterShift) {
                url += `&shift=${filterShift}`; 
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setMilkCollections(data.data.data || []);
                setTotalPages(data.data.last_page || 1);
                setTotalLitres(data.total_litres || 0);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to fetch milk collections");
            }
        } catch (error) {
            setError("An error occurred while fetching milk collections.");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, filterDate, filterMonth, filterRoute, filterShift, navigate, token]);

    // Fetch routes on component mount
    useEffect(() => {
        fetchRoutes();
    }, [fetchRoutes]);

    // Fetch milk collections when dependencies change
    useEffect(() => {
        fetchMilkCollections();
    }, [fetchMilkCollections]);

    const toggleDropdown = (index) => {
        if (isDropdownOpen === index) {
            setIsDropdownOpen(null); // Close dropdown if the same one is clicked
        } else {
            setIsDropdownOpen(index); // Open the clicked dropdown
        }
    };

    const handleDeleteClick = (milkCollection) => {
        setSelectedMilkCollection(milkCollection);
        setIsConfirmDialogOpen(true);
    };

    const confirmDeleteMilkCollection = async () => {
        if (selectedMilkCollection) {
            try {
                const token = localStorage.getItem("access_token");
                const response = await fetch(`${API_URL}/milk-collections/${selectedMilkCollection.id}`, {
                    method: 'DELETE',
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    setNotification({ type: "success", message: 'Milk collection deleted successfully' });
                    fetchMilkCollections(); // Refresh the list
                } else if (response.status === 401) {
                    navigate("/logout");
                } else if (response.status === 404) {
                    setNotification({ type: "danger", message: 'Milk collection not found.' });
                } else {
                    setNotification({ type: "danger", message: 'There was an error.' });
                    throw new Error('Failed to delete milk collection');
                }
            } catch (error) {
                setNotification({ message: 'An error occurred while deleting the milk collection.', type: 'danger' });
                console.error("Error:", error);
            } finally {
                setIsConfirmDialogOpen(false);
                setSelectedMilkCollection(null);
            }
        }
    };

    const cancelDeleteMilkCollection = () => {
        setIsConfirmDialogOpen(false);
        setSelectedMilkCollection(null);
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1); // Reset to the first page when searching
    };

    const handleFilterDateChange = (event) => {
        setFilterDate(event.target.value);
        setCurrentPage(1); // Reset to the first page when applying a new filter
    };

    const handleFilterMonthChange = (event) => {
        setFilterMonth(event.target.value);
        setCurrentPage(1); // Reset to the first page when applying a new filter
    };

    const handleFilterRouteChange = (event) => {
        setFilterRoute(event.target.value);
        setCurrentPage(1); // Reset to the first page when applying a new filter
    };

    const handleFilterShiftChange = (event) => {
        setFilterShift(event.target.value);
        setCurrentPage(1); // Reset to the first page when applying a new filter
    };

    const clearFilters = () => {
        setSearchTerm("");
        setFilterDate("");
        setFilterMonth("");
        setFilterRoute("");
        setFilterShift("");
        setCurrentPage(1);
    };

    // Handle click outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen !== null) {
                const dropdown = document.querySelector(`.dropdown.is-active`);
                if (dropdown && !dropdown.contains(event.target)) {
                    setIsDropdownOpen(null);
                }
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [isDropdownOpen]);

    return (
        <div>
            <main>
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <span className="title">Milk Collection</span>
                        </div>
                    </div>
                    
                    <div className="level-item has-text-centered">
                        <div>
                            <p>Total Litres: {totalLitres}</p>
                        </div>
                    </div>
                    
                    <div className="level-right">
                        <p className="level-item">
                            <Link to="add" className="button primary-bg">
                                <i className="fa fa-plus" aria-hidden="true"></i> &nbsp; New
                            </Link>
                        </p>
                    </div>
                </nav>

                <h1>Milk Collection List</h1>

                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                {/* Filters */}
                <div className="field is-horizontal">
                    <div className="field-body">
                        <div className="field">
                            <label className="label">Search by Farmer Name or Number</label>
                            <div className="control">
                                <input
                                    className="input"
                                    type="text"
                                    placeholder="Search by farmer name or number"
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Filter by Route</label>
                            <div className="control">
                                <div className="select is-fullwidth">
                                    <select 
                                        value={filterRoute} 
                                        onChange={handleFilterRouteChange}
                                    >
                                        <option value="">All Routes</option>
                                        {routes.map((route) => (
                                            <option key={route.id} value={route.id}>
                                                {route.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Filter by Shift</label>
                            <div className="control">
                                <div className="select is-fullwidth">
                                    <select 
                                        value={filterShift} 
                                        onChange={handleFilterShiftChange}
                                    >
                                        {shifts.map((shift) => (
                                            <option key={shift.value} value={shift.value}>
                                                {shift.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Filter by Date</label>
                            <div className="control">
                                <input
                                    type="date"
                                    className="input"
                                    value={filterDate}
                                    onChange={handleFilterDateChange}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Filter by Month</label>
                            <div className="control">
                                <input
                                    type="month"
                                    className="input"
                                    value={filterMonth}
                                    onChange={handleFilterMonthChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Clear Filters Button */}
                <div className="field is-horizontal">
                    <div className="field-body">
                        <div className="field">
                            <div className="control">
                                <button
                                    className="button is-light"
                                    onClick={clearFilters}
                                    disabled={!searchTerm && !filterDate && !filterMonth && !filterRoute && !filterShift}
                                >
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <p>Loading milk collection...</p>
                ) : error ? (
                    <p className="has-text-danger">{error}</p>
                ) : (
                    <>
                        <table className="table is-striped is-fullwidth">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Farmer Name</th>
                                    <th>Litres</th>
                                    <th>Collection Date</th>
                                    <th>Shift</th>
                                    <th>Route</th>
                                    <th>Price at Sale</th>
                                    <th>Posted At</th>
                                    <th>Options</th>
                                </tr>
                            </thead>
                            <tbody>
                                {milkCollections.length > 0 ? (
                                    milkCollections.map((milkCollection, index) => (
                                        <tr key={milkCollection.id}>
                                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                                            <td><Link to={`/dashboard/farmers/${milkCollection?.farmer?.id}/details`} className="primary-text-color"> {milkCollection?.farmer?.first_name} {milkCollection?.farmer?.middle_name} {milkCollection?.farmer?.last_name} ( {milkCollection?.farmer?.farmer_number} ) </Link></td>
                                            <td>{milkCollection.litres}</td>
                                            <td>{formatToDayMonthYear(milkCollection.collection_date)}</td>
                                            <td>{milkCollection?.collection_shift || 'N/A'}</td>
                                            <td>{milkCollection?.farmer?.collection_route?.name || 'N/A'}</td>
                                            <td>{milkCollection.price_at_sale}</td>
                                            <td> {(formatTimestamp(milkCollection.created_at) || " ")} </td>
                                            <td>
                                                <div className={`dropdown is-right ${isDropdownOpen === index ? 'is-active' : ''}`}>
                                                    <div className="dropdown-trigger">
                                                        <button
                                                            className="button is-white"
                                                            aria-haspopup="true"
                                                            aria-controls="dropdown-menu"
                                                            onClick={() => toggleDropdown(index)}
                                                        >
                                                            <span className="icon">
                                                                <i className="fas fa-ellipsis-v"></i>
                                                            </span>
                                                        </button>
                                                    </div>
                                                    <div className="dropdown-menu" id="dropdown-menu" role="menu">
                                                        <div className="dropdown-content">
                                                            {canEdit(userRole) && (
                                                                <>
                                                                    <Link to={`${milkCollection.id}/update`} className="dropdown-item">
                                                                        <span className="icon">
                                                                            <i className="fas fa-edit"></i>
                                                                        </span>
                                                                        <span>Edit</span>
                                                                    </Link>
                                                                    {/* <Link className="dropdown-item" onClick={() => handleDeleteClick(milkCollection)}>
                                                                        <span className="icon">
                                                                            <i className="fas fa-trash-alt"></i>
                                                                        </span>
                                                                        <span>Remove</span>
                                                                    </Link> */}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="has-text-centered">No milk collections found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Pagination Controls */}
                        <nav className="pagination is-centered">
                            <button
                                className="pagination-previous"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span className="pagination-page">Page {currentPage} of {totalPages}</span>
                            <button
                                className="pagination-next"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </nav>
                    </>
                )}
            </main>
        </div>
    );
}

export default MilkCollections;